import i18n from '@/i18n'
import { cloneValue, getAtPath, setAtPath } from './configPaths'
import { matchesCondition, normalizeConditionalOptions, visibleOptions } from './conditions'
import { applyPreset, mergeGeneratedConfiguration } from './schemaV2'

const TEXT_TYPES = new Set(['text', 'textarea', 'rich-text'])

// AI policy is separate from manual editing and from immutable package limits.
export function applyGenerationResult(template, current, result) {
  const generated = cloneValue(result.configuration)
  let base = current
  const preset = generated.presetId ? template.presets?.[generated.presetId] : null
  if (generated.presetId && (!preset || preset.formatId !== current.format.id)) {
    throw new Error(i18n.t('editorV2.generation.errors.styleUnavailable'))
  }
  if (preset) {
    base = applyPreset(base, preset)
    // The preset decides what it sets, also where the agent wrote a value (Tone).
    for (const operation of preset.operations || []) {
      if (operation.op === 'set' && generated.values && operation.path in generated.values) {
        generated.values[operation.path] = cloneValue(operation.value)
      }
    }
  }
  // Fresh generated rows must not inherit an old toggle, URL, crop or the template's sample copy
  // (Swipe Ad's defaults carry "New product" and sample prices): copy the agent left out stays
  // empty. What generation never writes stays: a Reel's clip is the advertiser's upload.
  for (const collection of template.schema.collections || []) {
    const rows = generated.collections?.[collection.path]
    if (!Array.isArray(rows)) continue
    const fields = (collection.itemSections || []).flatMap((section) => section.fields || [])
    const kept = fields.filter((field) => field.type === 'video-editor')
    base = setAtPath(base, collection.path, rows.map((generatedRow, index) => {
      let row = {
        ...cloneValue(collection.itemDefaults),
        [collection.identityPath || 'id']: getAtPath(current, `${collection.path}.${index}.${collection.identityPath || 'id'}`)
          || `${collection.id}-${index + 1}`,
      }
      fields.filter((field) => TEXT_TYPES.has(field.type) && !(field.path in (generatedRow || {})))
        .forEach((field) => { row = setAtPath(row, field.path, '') })
      kept.forEach((field) => {
        const value = getAtPath(current, `${collection.path}.${index}.${field.path}`)
        if (value !== undefined) row = setAtPath(row, field.path, cloneValue(value))
      })
      return row
    }))
  }
  if (result.briefing_version !== 'v2') {
    // Without a generation profile the agent writes the copy it has evidence for; an optional
    // text it left out (Scratch's body line) must not keep the template's sample sentence.
    for (const section of template.schema.sections || []) {
      for (const field of section.fields || []) {
        if (!TEXT_TYPES.has(field.type) || field.validation?.required || !field.path.startsWith('data.')) continue
        if (!(field.path in (generated.values || {}))) base = setAtPath(base, field.path, '')
      }
    }
  }
  let next = mergeGeneratedConfiguration(template.schema, base, generated)
  if (preset) next = setAtPath(next, 'presetId', preset.id)
  // What the agent wrote, by full path. Only these can be the agent's mistake.
  const written = new Set(Object.keys(generated.values || {}))
  for (const collection of template.schema.collections || []) {
    const rows = generated.collections?.[collection.path]
    if (!Array.isArray(rows)) continue
    rows.forEach((row, index) => Object.keys(row || {}).forEach((key) => written.add(`${collection.path}.${index}.${key}`)))
  }
  // A value the agent did not write can stop being available because of one it did: the
  // panel shape "Full Screen" exists only for the full-image layout, so a stored shape
  // broke every generation that chose another layout (Hellabrunn Zoo, Split Slider). Those
  // move to the first option still available, as after a format change; the agent's own
  // values are kept as written and checked below.
  const settled = normalizeConditionalOptions(template.schema, next)
  next = [...written].reduce((config, path) => {
    const value = getAtPath(next, path)
    return value === undefined ? config : setAtPath(config, path, value)
  }, settled)
  // A real option the agent chose can still be ruled out by another value: a Story Shape belongs
  // to one Type. A Type the agent did not write follows its Shape; when it wrote both, the Shape
  // settles like a stale value instead of failing the whole generation.
  const reconcile = (field, path, item) => {
    if (!['select', 'segmented'].includes(field.type) || !written.has(path)) return
    const value = getAtPath(next, path)
    const option = (field.options || []).find((entry) => entry.value === value)
    const available = (config) => visibleOptions(field.options, config, item).some((entry) => entry.value === value)
    if (!option || available(next)) return
    const condition = option.availableWhen
    if (condition?.path && condition.path !== 'format.id' && 'equals' in condition && !written.has(condition.path)) {
      const followed = setAtPath(next, condition.path, condition.equals)
      if (available(followed)) {
        next = followed
        return
      }
    }
    written.delete(path)
  }
  const check = (field, path, item) => {
    if (!matchesCondition(field.visibleWhen, next, item)) return
    const value = getAtPath(next, path)
    if (value == null || value === '') return
    if (field.type === 'boolean' && typeof value !== 'boolean') {
      throw new Error(i18n.t('editorV2.generation.errors.invalidSwitch', { label: field.label }))
    }
    // The generator switches "No Logo" to Favicon after the agent chose a Position, and a
    // favicon has no Auto or Center (split-slider 3.7.0): identity options settle instead.
    if (['select', 'segmented'].includes(field.type) && written.has(path) && !path.startsWith('data.brand.')
        && !visibleOptions(field.options, next, item).some((option) => option.value === value)) {
      throw new Error(i18n.t('editorV2.generation.errors.unavailableOption', { label: field.label }))
    }
  }
  const eachField = (visit) => {
    for (const section of template.schema.sections || []) {
      for (const field of section.fields || []) visit(field, field.path, null)
    }
    for (const collection of template.schema.collections || []) {
      getAtPath(next, collection.path, []).forEach((item, index) => {
        for (const section of collection.itemSections || []) {
          for (const field of section.fields || []) visit(field, `${collection.path}.${index}.${field.path}`, item)
        }
      })
    }
  }
  eachField(reconcile)
  eachField(check)
  return normalizeConditionalOptions(template.schema, next)
}
